import {DirectoryRichtextContent} from '@github-ui/code-view-shared/components/directory/DirectoryRichtextContent'
import {EditButton} from '@github-ui/code-view-shared/components/EditButton'
import TableOfContentsPanel from '@github-ui/code-view-shared/components/TableOfContentsPanel'
import {useCopilotSettings} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {useEditPermissions} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import type {PanelType, ReadmeBlobPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {repositoryTreePath} from '@github-ui/paths'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Heading, Link} from '@primer/react'
import {clsx} from 'clsx'

import {AskCopilotReadmeMenu} from '../../ask-copilot/AskCopilotReadmeMenu'
import TableOfContents from '../../headers/header-components/TableOfContents'
import {Panel} from '../../Panel'
import styles from './DirectoryReadmePreview.module.css'

export function DirectoryReadmePreview({
  openPanel,
  readme,
  setOpenPanel,
  stickyHeaderHeight,
}: {
  openPanel: PanelType | undefined
  readme: ReadmeBlobPayload
  setOpenPanel: (panel: PanelType | undefined) => void
  stickyHeaderHeight?: number
}) {
  const {displayName, errorMessage, richText, headerInfo, timedOut} = readme
  const {toc} = headerInfo || {}
  const repo = useCurrentRepository()
  const path = usePath()
  const refInfo = useRefInfo()
  const {canEdit} = useEditPermissions()
  const {copilotAccessAllowed} = useCopilotSettings()
  const readmePath = path && path !== '/' ? `${path}/${displayName}` : displayName

  return (
    <div className={styles.readmeWrapper}>
      <div
        id="readme"
        className={clsx(
          styles.readmeContainer,
          openPanel === 'toc' ? styles.readmeContainerWithToc : styles.readmeContainerFull,
        )}
      >
        <div className={styles.readmeHeader}>
          <Heading as="h2" className={styles.readmeHeading}>
            <Link className={styles.readmeLink} href="#readme">
              {displayName}
            </Link>
          </Heading>
          {canEdit && (
            <EditButton
              editPath={repositoryTreePath({repo, commitish: refInfo.name, action: 'edit', path: readmePath})}
              editTooltip="Edit README"
            />
          )}
          <TableOfContents toc={toc} openPanel={openPanel} setOpenPanel={setOpenPanel} isDirectoryReadme />
          {copilotAccessAllowed && (
            <ErrorBoundary boundaryName="ask-copilot-readme-header" fallback={null}>
              <AskCopilotReadmeMenu />
            </ErrorBoundary>
          )}
        </div>
        <DirectoryRichtextContent
          richText={richText}
          errorMessage={errorMessage}
          path={readmePath}
          stickyHeaderHeight={stickyHeaderHeight}
          timedOut={timedOut}
        />
      </div>

      {openPanel === 'toc' && (
        <Panel style={{height: 'fit-content', width: '35%'}}>
          <TableOfContentsPanel
            onClose={() => {
              setOpenPanel(undefined)
            }}
            toc={toc}
          />
        </Panel>
      )}
    </div>
  )
}
